import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  ChevronsRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  LockIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";

import { languages, setInterpolationDictionary } from "simplydo/core/i18n";
import { OpenAPI } from "simplydo/interfaces";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField } from "simplydo/react/ui/form";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import ConnectionsManager from "components/lib/ConnectionsManager";
import { FakeLink } from "components/lib/UI";

const isValidPhoneNumber = (number: string) =>
  import("libphonenumber-js")
    .then((lib) => lib.isValidNumber(number))
    .catch(() => true);

const formatNumber = (number: string) => {
  let formattedNumber = number;
  if (number.indexOf("07") === 0) {
    formattedNumber = formattedNumber.replace("07", "+447");
  }
  const regex = /[^0-9]/g;
  const cleanNumber = formattedNumber.replace(regex, "");
  "".slice();
  if (formattedNumber.startsWith("+")) {
    return `+${cleanNumber.slice(0, 2)} ${cleanNumber.slice(2)}`;
  }
  return cleanNumber;
};

export function UserSettingsAccount() {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onSwitchOrganisation = useCallback(
    (org: OpenAPI.Schemas["Organisation"]) =>
      dispatch(actions.user.switchOrganisation(org)),
    [dispatch],
  );
  const onPhoneNumbersChanged = useCallback(
    (phoneNumbers: OpenAPI.Schemas["User"]["phoneNumbers"]) =>
      dispatch(actions.user.updatePhoneNumbers(phoneNumbers)),
    [dispatch],
  );
  const reloadUser = useUserReload();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountString, setDeleteAccountString] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [formattedNewNumber, setFormattedNewNumber] = useState("");
  const [verificationEntry, setVerificationEntry] = useState<{
    number: string;
  } | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  );
  const [validNumber, setValidNumber] = useState(false);

  const phoneNumbers = useMemo(
    () => user?.phoneNumbers || [],
    [user?.phoneNumbers],
  );

  useEffect(() => {
    const newNumber = formatNumber(newPhoneNumber);
    setFormattedNewNumber(newNumber);
    isValidPhoneNumber(newNumber)
      .then((valid) => {
        setValidNumber(valid);
      })
      .catch(() => {
        setValidNumber(true);
      });
  }, [newPhoneNumber]);

  const addNumber = useCallback(() => {
    util
      .confirm(
        "Confirm phone number",
        `We are about to send a verification code to '${formattedNewNumber}'. Please make sure you entered it correctly.`,
      )
      .then(() => {
        api.auth.addPhoneNumber(
          formattedNewNumber,
          (newNumber) => {
            onPhoneNumbersChanged([...phoneNumbers, newNumber]);
            setNewPhoneNumber("");
            setVerificationEntry(newNumber);
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [formattedNewNumber, onPhoneNumbersChanged, phoneNumbers]);

  const verify = useCallback(() => {
    if (!verificationEntry) {
      return;
    }
    setVerificationLoading(true);
    api.auth.verifyPhoneNumber(
      verificationEntry.number,
      verifyCode,
      () => {
        setVerificationLoading(false);
        setVerificationEntry(null);
        setVerifyCode("");
        toast.success("Phone number verified!");

        onPhoneNumbersChanged(
          phoneNumbers.map((pn) => {
            if (pn.number === verificationEntry.number) {
              return { number: pn.number, verified: true };
            }
            return pn;
          }),
        );
      },
      (e) => {
        setVerificationLoading(false);
        toast.error("Verification failed.");
        setVerificationError(e.message);
      },
    );
  }, [verificationEntry, verifyCode, onPhoneNumbersChanged, phoneNumbers]);

  const sendNewCode = useCallback(() => {
    if (!verificationEntry) {
      return;
    }
    util
      .confirm(
        "Generate new code",
        `We are about to send a new verification code to '${verificationEntry.number}'. This will invalidate the previous code. Proceed?`,
      )
      .then(() => {
        api.auth.resendPhoneNumberVerification(
          verificationEntry.number,
          () => {
            toast.success("Sent new verification token.");
          },
          () => {
            toast.error(
              "Failed to generate new verification token, please contact an administrator.",
            );
          },
        );
      })
      .catch(() => {});
  }, [verificationEntry]);

  const deleteAccount = () => {
    if (!user?._id) return;
    if (deleteAccountString === "DELETE") {
      util
        .confirm(
          t("users.account.options.delete.confirm.title"),
          t("users.account.options.delete.confirm.info"),
        )
        .then(() => {
          setDeletingAccount(true);
          api.users.delete(
            user._id,
            {},
            () => {
              toast(t("users.account.options.delete.confirm.success"));
              setErrorMessage("");
              setDeletingAccount(false);

              api.token = null;
              api.options?.onLogout?.();

              navigate("/");
            },
            (err) => {
              setErrorMessage(err.message);
              setDeletingAccount(false);
            },
          );
        })
        .catch(() => {});
    } else {
      setErrorMessage('Please type "DELETE" to delete your account');
      setDeletingAccount(false);
    }
  };

  const switchOrganisation = (org: OpenAPI.Schemas["Organisation"]) => {
    if (!user?._id) return;
    api.users.switchOrganisation(
      user._id,
      org._id,
      (data) => {
        onSwitchOrganisation(data.organisation);
      },
      (err) => toast.error(err.message),
    );
  };

  const leaveOrganisation = (org: OpenAPI.Schemas["Organisation"]) => {
    util
      .confirm(
        "Really leave this organisation?",
        "You may not be able to re-join automatically.",
      )
      .then(() => {
        if (!user?._id) return;
        api.users.leaveOrganisation(
          user._id,
          org._id,
          () => {},
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  const changeLanguage = (language: string) => {
    if (!user?._id) return;
    const currentLanguage = i18n.language;
    i18n.changeLanguage(language);
    setInterpolationDictionary(user?.ownerOrganisation?.terminology);
    api.users.updateLanguagePreferences(
      user._id,
      language,
      () => {},
      (err) => {
        i18n.changeLanguage(currentLanguage);
        setInterpolationDictionary(user?.ownerOrganisation?.terminology);
        toast.error(err.message);
      },
    );
  };

  if (!user) return null;

  return (
    <Container>
      <Modal open={!!verificationEntry}>
        <ModalHeader>
          <Icon icon={LockIcon} style={{ paddingRight: "5px" }} />
          Verify phone number
          <br />
        </ModalHeader>
        <ModalContent>
          <p>
            To complete the verification process, please enter the six digit
            one-time code which was sent to &quot;{verificationEntry?.number}
            &quot;.
          </p>
          <p>
            Please be aware that verification codes are only valid for one hour.
            If you missed yours or if it expired please{" "}
            <a style={{ cursor: "pointer" }} href="#a" onClick={sendNewCode}>
              click here
            </a>{" "}
            to receive a new code for this number.
          </p>
          {verificationError ? (
            <Message color="red">{verificationError}</Message>
          ) : null}
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.keyCode === 13) verify();
              }}
              onChange={(e) => setVerifyCode(e.target.value)}
              value={verifyCode}
              placeholder="123456"
              size="large"
              autoFocus
            />
          </div>
          <div style={{ textAlign: "center", marginTop: 5 }}>
            <FakeLink withBorder onClick={() => sendNewCode()}>
              Resend verification code
            </FakeLink>
          </div>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              setVerificationEntry(null);
              setVerifyCode("");
            }}
          >
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={() => verify()}
            loading={verificationLoading}
            disabled={verifyCode.length !== 6}
            color="primary"
          >
            Continue
          </Button>
        </ModalActions>
      </Modal>

      <H3>
        {t("users.account.options.orgs.title")}
        <Subheader>
          {t("users.account.options.orgs.info", { appName: util.appName() })}
        </Subheader>
      </H3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              {t("users.account.options.orgs.list.name")}
            </TableHeaderCell>
            <TableHeaderCell />
          </TableRow>
        </TableHeader>
        <TableBody>
          {user.ownerOrganisations?.map((o) => (
            <TableRow key={o._id}>
              <TableCell>{o.name}</TableCell>
              <TableCell collapsing textAlign="right">
                {o._id === user.ownerOrganisation?._id ? (
                  <Label size="small" color="green">
                    {t("users.account.options.orgs.list.primary")}
                  </Label>
                ) : (
                  <>
                    <Button
                      size="small"
                      onClick={() => switchOrganisation(o)}
                      variant="outline"
                    >
                      {t("users.account.options.orgs.list.makePrimary")}
                    </Button>
                    <Button
                      color="orange"
                      size="small"
                      onClick={() => leaveOrganisation(o)}
                      variant="outline"
                    >
                      Leave
                    </Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Message size="small">{t("users.account.options.orgs.message")}</Message>
      <Divider />

      <H3>
        {t("users.preferences.language.title")}
        <Subheader>{t("users.preferences.language.info")}</Subheader>
      </H3>
      <Dropdown
        variant="outline"
        fluid
        value={i18n.language}
        options={languages.map((l) => ({
          flag: l.flag,
          key: l.key,
          value: l.key,
          text: l.name,
        }))}
        onChange={(_e, s) => changeLanguage(s.value as string)}
      />
      <Divider />

      <H3>
        {t("users.profile.communication.phone.title")}
        <Subheader>{t("users.profile.communication.phone.info")}</Subheader>
      </H3>
      <Table basic fixed padded>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              {t("users.profile.communication.phone.table.number")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("users.profile.communication.phone.table.verification")}
            </TableHeaderCell>
            <TableHeaderCell textAlign="right"></TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {phoneNumbers.map((entry) => (
            <TableRow key={entry.number}>
              <TableCell>{entry.number}</TableCell>
              <TableCell>
                {entry.verified ? (
                  <>
                    <Icon icon={CircleCheckIcon} style={{ paddingRight: 5 }} />
                    Verified
                  </>
                ) : (
                  <>
                    <Icon icon={XIcon} style={{ paddingRight: 5 }} />
                    Not verified
                    <Button
                      style={{ marginLeft: 10 }}
                      size="small"
                      onClick={() => {
                        setVerificationEntry(entry);
                      }}
                      variant="outline"
                    >
                      {"Verify now"}
                    </Button>
                  </>
                )}
              </TableCell>
              <TableCell textAlign="right">
                <Button
                  size="small"
                  color="red"
                  onClick={() => {
                    util
                      .confirm(
                        "Remove number",
                        `Are you sure you want to remove '${formatNumber(entry.number)}'?`,
                      )
                      .then(() => {
                        api.auth.removePhoneNumber(
                          entry.number,
                          () => {
                            onPhoneNumbersChanged(
                              phoneNumbers.filter(
                                (pn) => pn.number !== entry.number,
                              ),
                            );
                          },
                          (err) => toast.error(err.message),
                        );
                      })
                      .catch(() => {});
                  }}
                  variant="outline"
                >
                  {t("generic.remove")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <FormField
        error={
          newPhoneNumber === "" || validNumber
            ? undefined
            : t("users.profile.communication.phone.table.placeholder")
        }
      >
        <Input
          fluid
          type="text"
          placeholder={t("users.profile.communication.phone.table.info2")}
          value={newPhoneNumber}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNewPhoneNumber(event.target.value);
          }}
          error={!(newPhoneNumber === "" || validNumber)}
          action={
            <Button
              disabled={!validNumber}
              labelPosition="right"
              onClick={() => addNumber()}
            >
              Add number
              <Icon icon={ChevronsRightIcon} />
            </Button>
          }
        />
      </FormField>

      {user?.ownerOrganisation?.enabledFeatures?.includes(
        "teamsHandleAdvertising",
      ) ? (
        <>
          <Divider section />

          <ConnectionsManager
            entity="user"
            entityId={user._id}
            serviceProvider="microsoft"
            service="microsoftTeams"
            contextId="userIntegration"
            title="Microsoft Teams"
            subtitle="Connect your Microsoft Teams account to advertise your Teams handle. Once connected, other users will be able to message you directly via Teams."
            connectionButtonText="Connect Microsoft Teams"
            onDisconnect={() => {
              reloadUser();
            }}
          />
          <Message>
            Please note that your email address forms part of your Teams handle
            link and may be viewable to others.
          </Message>
        </>
      ) : null}

      <Divider section />
      <ConnectionsManager
        entity="user"
        entityId={user._id}
        serviceProvider="microsoft"
        service="office365"
        contextId="userIntegration"
        title="Microsoft Office365"
        subtitle="Connect your Microsoft account to let you attach files from your OneDrive when uploading content in the platform."
        connectionButtonText="Connect Microsoft Office365"
      />

      <Divider section />
      <H3>
        Data protection complaints
        <Subheader>
          If you would like to make a data protection complaint to SimplyDo,
          please tap the button below to find out more about how you can do so.
        </Subheader>
      </H3>
      <ButtonLink
        style={{ maxWidth: "fit-content" }}
        target="_blank"
        to="https://support.simplydo.co.uk/hc/simplydo-help-center/articles/1776680509-data-protection-complaints"
      >
        Make a complaint
      </ButtonLink>

      {!user.ownerOrganisation?.disableAccountSelfDeletion ? (
        <>
          <Divider />
          <H3>
            {t("users.account.options.delete.title")}
            <Subheader>
              {t("users.account.options.delete.info", {
                appName: util.appName(),
              })}
            </Subheader>
          </H3>
          <Label color="red">
            <Icon icon={TriangleAlertIcon} />{" "}
            {t("users.account.options.delete.warningLabel")}
          </Label>
          <Message color="orange">
            {t("users.account.options.delete.warningMessage")}
          </Message>
          <p>{t("users.account.options.delete.enterPassword")}</p>
          {errorMessage && !deletingAccount && (
            <Message size="small" color="red">
              <Icon icon={CircleAlertIcon} />
              <MessageHeader>{errorMessage}.</MessageHeader>
            </Message>
          )}
          <Input
            fluid
            value={deleteAccountString}
            action={
              <Button color="red" onClick={deleteAccount}>
                {t("users.account.options.delete.confirm.title")}
              </Button>
            }
            placeholder="DELETE"
            onChange={(e) => setDeleteAccountString(e.target.value)}
          />
        </>
      ) : null}
    </Container>
  );
}

export default UserSettingsAccount;
